import FullCalendar from '@fullcalendar/react'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useSearchParams } from 'react-router-dom'

import type { DateRangeQueryParams } from '@/services/types'
import type { enumOrderStatuses } from '@/types/tenant/workGroup.types'
import { enumStatysClasses } from '@/types/tenant/workGroup.types'

import Calendar2Icon from '../../assets/img/ico-calendar-2.svg?react'
import ChevronNextIcon from '../../assets/img/ico-chevron-next-2.svg?react'
import ChevronPrevIcon from '../../assets/img/ico-chevron-prev-2.svg?react'
import Accordion from '../common/Accordion'

import 'react-datepicker/dist/react-datepicker.css'
import { getStartAndEndOfDay, getStartAndEndOfWeek } from '@/utils/helpers'

export interface FullCalendarApi extends Record<string, any> {
    prev: () => void
    next: () => void
    changeView: (viewName: string) => void
    getDate: () => Date
    gotoDate: (date: Date) => void
}

export interface CalendarResource {
    id: string
    extendedProps?: {
        photo: string
        fullName: string
    }
}

export interface CalendarEvent {
    id: string
    start: string
    end: string
    resourceId: string
    extendedProps: {
        status: `${enumOrderStatuses}`
        title: string
        note?: string
    }
}

interface CalendarProps {
    title?: string
    children?: ReactNode
    events?: CalendarEvent[]
    resources?: CalendarResource[]
    hideSidebar?: boolean
    setDateRage?: ({ startDate, endDate }: DateRangeQueryParams) => void
}

export default function Calendar({
    title = 'Calendar',
    children,
    events = [],
    resources = [],
    hideSidebar,
    setDateRage,
}: CalendarProps) {
    const views = resources.length
        ? (['resourceTimelineDay', 'resourceTimelineWeek'] as const)
        : (['timelineDay', 'timelineWeek'] as const)
    type ViewType = (typeof views)[number]

    const [view, setView] = useState<ViewType>(views[0])
    const [calendarApi, setCalendarApi] = useState<FullCalendarApi>()
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)

    const handlePrev = () => {
        if (!calendarApi) return
        calendarApi?.prev()
        setSelectedDate(calendarApi.getDate())
    }

    const handleNext = () => {
        if (!calendarApi) return
        calendarApi?.next()
        setSelectedDate(calendarApi.getDate())
    }

    const handleViewChange = (newView: ViewType) => {
        if (!calendarApi) return
        setView(newView)
        calendarApi?.changeView(newView)
        setSelectedDate(calendarApi.getDate())
    }

    useEffect(() => {
        if (!selectedDate) return
        const { startDate, endDate } = getStartAndEndOfDay(selectedDate)
        const { startDate: startDateWeek, endDate: endDateWeek } =
            getStartAndEndOfWeek(selectedDate)
        if (setDateRage) {
            console.log(
                startDateWeek,
                endDateWeek,
                'startDate: startDateWeek, endDate: endDateWeek',
            )

            setDateRage({
                startDate: ['resourceTimelineWeek', 'timelineWeek'].includes(
                    view,
                )
                    ? startDateWeek
                    : startDate,
                endDate: ['resourceTimelineWeek', 'timelineWeek'].includes(view)
                    ? endDateWeek
                    : endDate,
            })
        }
    }, [selectedDate])

    return (
        <div className="col-12">
            <Accordion
                header={
                    <div className="flex justify-between flex-auto">
                        <div className="c-h-item">
                            <div className="a-h-item--sm">
                                <div className="calendar-info">{title}</div>
                            </div>
                            <div className="calendar-title">
                                <div className="flex items-center">
                                    <div style={{ width: 116, height: 24 }}>
                                        <DatePicker
                                            selected={
                                                selectedDate || new Date()
                                            }
                                            className="calendar-title"
                                            onChange={(date: Date | null) => {
                                                setSelectedDate(date)
                                                if (calendarApi && date) {
                                                    calendarApi.gotoDate(date)
                                                }
                                            }}
                                            dateFormat="MMM d, yyyy"
                                        />
                                    </div>
                                    <span className="ico absolute right-4">
                                        <Calendar2Icon />
                                    </span>
                                </div>
                            </div>
                        </div>
                        {calendarApi && (
                            <div className="c-h-item">
                                <label className="calendar-mode" id="mode-days">
                                    {views.map((item, index) => (
                                        <label
                                            key={index}
                                            className="calendar-mode-item"
                                        >
                                            <input
                                                type="radio"
                                                checked={view === item}
                                                hidden
                                                onChange={() =>
                                                    handleViewChange(item)
                                                }
                                            />
                                            <div className="calendar-mode-btn btn">
                                                {item.replace(
                                                    resources?.length
                                                        ? 'resourceTimeline'
                                                        : 'timeline',
                                                    '',
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </label>
                                <div className="group-button-calendar">
                                    <button
                                        type="button"
                                        className="btn btn--square btn--xs full-radius btn--secondary-2"
                                        onClick={handlePrev}
                                    >
                                        <span className="ico">
                                            <ChevronPrevIcon />
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--square btn--xs full-radius btn--secondary-2"
                                        onClick={handleNext}
                                    >
                                        <span className="ico">
                                            <ChevronNextIcon />
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                }
            >
                <div
                    className="full-calendar full-calendar__users"
                    style={{ height: 600 }}
                >
                    <FullCalendar
                        plugins={[resourceTimelinePlugin]}
                        resources={resources}
                        initialView={views[0]}
                        initialDate={new Date()}
                        resourceAreaWidth={
                            !resources?.length || hideSidebar ? 0 : 200
                        }
                        events={events}
                        resourceLabelContent={({ resource }) => {
                            if (resource.extendedProps) {
                                const { fullName, photo } =
                                    resource.extendedProps as Required<CalendarResource>['extendedProps']
                                return (
                                    <ResourceLabelContent
                                        fullName={fullName}
                                        photo={photo}
                                    />
                                )
                            }
                        }}
                        eventContent={({ event }) => {
                            const { status, title, note } =
                                event.extendedProps as CalendarEvent['extendedProps']
                            return <EventContent {...{ status, title, note }} />
                        }}
                        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
                        ref={(calendarRef) =>
                            setCalendarApi(
                                calendarRef?.getApi() as FullCalendarApi,
                            )
                        }
                        headerToolbar={false}
                        resourceAreaHeaderContent={false}
                        height={'100%'}
                        views={{
                            resourceTimelineDay: {
                                slotLabelFormat: {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    meridiem: 'short',
                                },
                            },
                            resourceTimelineWeek: {
                                slotLabelFormat: [
                                    {
                                        weekday: 'long', // Полное название дня недели
                                    },
                                    {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        meridiem: 'short',
                                    },
                                ],
                            },
                            day: { slotMinWidth: 50 },
                            week: { slotMinWidth: 100 },
                        }}
                    />
                </div>
                {children}
            </Accordion>
        </div>
    )
}

function ResourceLabelContent({
    fullName,
    photo,
}: Required<CalendarResource>['extendedProps']) {
    return (
        <div className="user-calendar-resource">
            <div className="ico-avatar btn btn--square user-calendar-ava">
                <img src={photo} alt="user-photo" />
            </div>
            <div className="user-calendar-info">
                <div className="user-calendar-name">
                    <p>{fullName}</p>
                </div>
                {/* <div className="user-calendar-desk">
                    <span className="user-calendar-desk-percent">
                        {bookedTime} booked
                    </span>
                </div> */}
            </div>
        </div>
    )
}

function EventContent({ status, title, note }: CalendarEvent['extendedProps']) {
    return (
        <div
            className={clsx(
                'event-calendar-resource',
                enumStatysClasses[status],
            )}
        >
            <div className="event-calendar-status"></div>
            <div className="event-calendar-content">
                <p className="event-calendar-title">{title}</p>
                <p className="event-calendar-desk">{note}</p>
            </div>
        </div>
    )
}
